import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
// import images
import bannerImg from "../../image/newsroom/hightech-conference-room-with-video-screens.jpg";
// import data
import contentData from "../../data/content/newsroom/newsContent.json";
import { DataKeys, DataStructure, DataFlat } from "../../types";
// import components
import { MainFrame, SpacingFrame } from "../display/Frame";
import { BannerSection } from "../display/Sections";
import { TextBody, TextBulletList, TitleSubtitle, TextNumberedList } from "../display/TextFormat";
import { BlogCard, NewsCard } from "../display/Cards";
import { MoreButton, PageControl } from "../tools/Buttons";
import { VideoPlayer } from "../tools/Viewer";
import { NavTree } from "../navigation/NavLinkPages";
import { ShowMorePosts } from "../content/BlogNews";
import NoContent from "../content/NoContent";
// import functions
import { scrollTop } from "../../functions/DisplayFunctions";
import { filterDictionaryByKeys, subsetListByChunk } from "../../functions/DataFunctions";
// import context
import { useLang } from "../Context";
// import global var
import { typedVocab } from "../GlobalVar";


const typedContent : DataStructure = contentData;
const imagePath = "image/newsroom/";


export default function NewsRoom() {
    const { lang } = useLang();
    const content = typedContent["content"];
    const contentKeys = Object.keys(content);

    const [selPage, setSelPage] = useState<number>(1);
    const [contentData, setContentData] = useState<DataStructure>(content);

    useEffect(() => {
        scrollTop();
        let subList = subsetListByChunk(contentKeys, selPage, 6);
        let data = filterDictionaryByKeys(subList, content);
        setContentData(data);
    }, [selPage]);


    let posts = Object.keys(contentData).map((i) => {
        let data = content[i];
        let link = "/newsroom/" + i;
        return (
            <NewsCard data={data} imagePath={imagePath} link={link} />
        )
    });

    return (
        <>
            <DefaultBanner />
            <NavTree />

            <MainFrame>
                <SpacingFrame>
                    <TitleSubtitle title={ typedVocab["Latest Press Release"][lang] } />

                    <div className="flex flex-col divide-y-1 divide-teal-100/50 border-y-1 border-teal-100/50">
                        { posts }
                    </div>

                    <PageControl contentListLen={contentKeys.length} divider={6} sendSelPage={setSelPage} />
                </SpacingFrame>
            </MainFrame>
        </>
    )
};


export function NewsSubpages() {
    const { lang } = useLang();
    const { newsStories } = useParams();

    const data = newsStories
        ? (typedContent["content"][newsStories] as DataStructure | undefined)
        : undefined;

    if (!data) {
        return <NoContent />;
    }

    const navTitle = data["title"][lang] as string | undefined;

    return (
        <>
            <DefaultBanner />
            {navTitle && <NavTree addLink={navTitle} />}
            <NewsContent data={data} link={newsStories} />
        </>
    );
}


//////////////////////
// LOCAL FUNCTIONS //
/////////////////////
function DefaultBanner() {
    const { lang } = useLang();
    return (
        <BannerSection image={bannerImg} title={ typedContent["title"][lang] } subtitle={ typedContent["subtitle"][lang] } />
    )
};

interface NewsContentProps {
    data: DataKeys;
    link: string | undefined;
};
function NewsContent({ data, link } : NewsContentProps) {
    const { lang } = useLang();

    const title = data["title"][lang] as string;
    const author = data["author"][lang] as string;
    const date = data["date"][lang] as string;
    const contentData = data["content"] as DataStructure;
    const buttonData = data["button"] as DataFlat;
    const showButton = Boolean(buttonData?.["label"]);
    const hasVideo = Object.keys(data).includes("video");
    const videoUrl = hasVideo ? (data["video"] as string) : undefined;
    const imageName =
        !hasVideo && data["image"] !== ""
            ? require("../../" + imagePath + data["image"])
            : undefined;

    let content = Object.keys(contentData).map((i) => {
        let textData = contentData[i];
        let isBullet = textData["bullet"];
        let isNumber = textData["number"];
        let title = textData["title"][lang];
        let text = textData["text"][lang];

        if (isBullet) {
            return (
                <TextBulletList subtitle={title} list={text} />
            )
        } else if (isNumber) {
            return (
                <TextNumberedList title={title} text={text} />
            )
        } else {
            return (
                <>
                    { title && <p className="text-left font-bold text-grey-800 -mb-5">{title}</p> }
                    <TextBody text={text} />
                </>
            )
        };
    });

    return (
        <MainFrame>
            <SpacingFrame spacing={128}>
                <SpacingFrame>
                    <div className="flex flex-col">
                        <TitleSubtitle title={ title } left />
                        <p className="text-left text-grey-400">
                            <span>{ author } | </span>
                            <span>{ date }</span>
                        </p>
                    </div>
                    <SpacingFrame spacing={24}>
                        { imageName && <img className="w-full max-h-[600px] object-cover bg-center flex-shrink-0" src={imageName} /> }
                        { videoUrl && <VideoPlayer url={videoUrl} height={600} /> }
                        { content }
                        { showButton && <MoreButton label={ buttonData["label"] } link={ buttonData["link"] } newTab /> }
                    </SpacingFrame>
                </SpacingFrame>

                <ShowMorePosts currPost={link} type="newsroom" />
            </SpacingFrame>
        </MainFrame>
    )
};